import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchCrewsAndEmployees } from '../../api/jobWorkflow'
import type { EmployeeOption } from '../../api/jobWorkflow'
import { formatEmployeeLabel } from '../../lib/formatEmployeeLabel'

export interface PropertyAssignmentData {
  default_assigned_crew_id: string
  default_assigned_employee_id: string
}

interface PropertyAssignmentFieldsProps {
  value: PropertyAssignmentData
  onChange: (value: PropertyAssignmentData) => void
}

export function PropertyAssignmentFields({ value, onChange }: PropertyAssignmentFieldsProps) {
  const [crews, setCrews] = useState<{ id: string; name: string }[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])

  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    fetchCrewsAndEmployees().then(({ crews: c, employees: e, loadError: err }) => {
      setCrews(c)
      setEmployees(e)
      if (err) setLoadError(err)
      else if (c.length === 0 && e.length === 0) setLoadError('empty')
      else setLoadError(null)
    })
  }, [])

  const set = (patch: Partial<PropertyAssignmentData>) => onChange({ ...value, ...patch })

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Default crew and employee for contracts that use &quot;Property default&quot; assignment.
        Changing these updates future open jobs on those contracts.
      </p>
      {loadError && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {loadError === 'empty' ? (
            <>
              No crews or employees found.{' '}
              <Link to="/settings/team" className="underline font-medium">
                Add them in Team
              </Link>
              .
            </>
          ) : (
            loadError
          )}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Crew</label>
          <select
            value={value.default_assigned_crew_id || ''}
            onChange={(e) => set({ default_assigned_crew_id: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Unassigned</option>
            {crews.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Employee</label>
          <select
            value={value.default_assigned_employee_id || ''}
            onChange={(e) => set({ default_assigned_employee_id: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Unassigned</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {formatEmployeeLabel(e)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
